import { useMemo, useState } from "react";
import { Alert, Button, Modal, Space, Table, Tag, Tooltip, Typography, Upload } from "antd";
import {
    CheckCircleOutlined, CloseCircleOutlined, DownloadOutlined, FileExcelOutlined,
    InboxOutlined, UploadOutlined,
} from "@ant-design/icons";
import * as XLSX from "xlsx";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import axiosInstance from "../../../../../variables/api/axiosInstance";

const { Text, Title } = Typography;
const { Dragger } = Upload;

// Colonnes du modèle → champs NewVictimeDTO. `header` = intitulé de la colonne dans le fichier.
const COLS = [
    { key: "nom", header: "Nom", required: true },
    { key: "prenom", header: "Prénom" },
    { key: "sexe", header: "Sexe (M/F)", kind: "sexe" },
    { key: "dateNaissance", header: "Date de naissance (JJ/MM/AAAA)", kind: "date" },
    { key: "lieuNaissance", header: "Lieu de naissance" },
    { key: "telephone", header: "Téléphone" },
    { key: "adresse", header: "Adresse" },
    { key: "email", header: "Email" },
    { key: "profession", header: "Profession" },
    { key: "casVictime", header: "Cas (BLESSE/DECEDE/AUTRE)", required: true, kind: "cas" },
    { key: "graviteBlessure", header: "Gravité blessure" },
    { key: "descriptionDegatsVictime", header: "Description" },
    { key: "adversaire", header: "Adversaire (OUI/NON)", kind: "bool" },
    { key: "paysResidenceHabituel", header: "Pays de résidence" },
    { key: "revenuProfessionelMensuelAvantAccident", header: "Revenu mensuel avant accident", kind: "num" },
    { key: "perteRevenuProfessionelMensuelApresAccident", header: "Perte revenu mensuel après", kind: "num" },
    { key: "montantBourseMensuelle", header: "Bourse mensuelle", kind: "num" },
];

// Normalise un intitulé de colonne : sans accents, minuscule, sans parenthèses ni ponctuation.
const norm = (s) =>
    `${s || ""}`
        .normalize("NFD").replace(/[̀-ͯ]/g, "")
        .toLowerCase()
        .replace(/\(.*?\)/g, "")
        .replace(/[^a-z0-9]/g, "")
        .trim();

const parseDate = (v) => {
    if (v == null || v === "") return null;
    if (typeof v === "number") {
        const d = XLSX.SSF.parse_date_code(v);
        if (!d) return null;
        return dayjs(`${d.y}-${String(d.m).padStart(2, "0")}-${String(d.d).padStart(2, "0")}`);
    }
    const s = `${v}`.trim();
    const parts = s.split(/[/.\-]/);
    if (parts.length === 3) {
        // JJ/MM/AAAA ou AAAA-MM-JJ
        const dt = parts[0].length === 4
            ? dayjs(`${parts[0]}-${parts[1].padStart(2, "0")}-${parts[2].padStart(2, "0")}`)
            : dayjs(`${parts[2]}-${parts[1].padStart(2, "0")}-${parts[0].padStart(2, "0")}`);
        return dt.isValid() ? dt : null;
    }
    const dt = dayjs(s);
    return dt.isValid() ? dt : null;
};

const normCas = (v) => {
    const s = norm(v);
    if (!s) return "";
    if (s.startsWith("bless")) return "BLESSE";
    if (s.startsWith("dece") || s.startsWith("deced") || s.startsWith("dcd")) return "DECEDE";
    if (s.startsWith("autre") || s.includes("materiel")) return "AUTRE";
    return `${v}`.trim().toUpperCase(); // laisse tel quel → sera invalidé si non conforme
};
const normSexe = (v) => {
    const s = norm(v);
    if (!s) return "";
    if (s === "m" || s.startsWith("masc") || s.startsWith("homme")) return "M";
    if (s === "f" || s.startsWith("femi") || s.startsWith("femme")) return "F";
    return "";
};
const normBool = (v) => {
    const s = norm(v);
    return ["oui", "yes", "1", "true", "vrai", "o", "x"].includes(s);
};
const normNum = (v) => {
    if (v == null || v === "") return null;
    const n = Number(`${v}`.replace(/\s/g, "").replace(",", "."));
    return Number.isFinite(n) ? n : null;
};

/**
 * Import Excel des VICTIMES d'un sinistre : modèle → upload → aperçu + vérification → enregistrement.
 * Chaque ligne valide est envoyée en lot à POST /api/victime/ind/batch/{sinistreCodeId}.
 */
const VictimesExcelImport = ({ open, setOpen, sinistre, setRefetch }) => {
    const [rows, setRows] = useState([]); // [{ __rowKey, __errors:[], nom, ... }]
    const [fileName, setFileName] = useState("");
    const [saving, setSaving] = useState(false);

    const reset = () => { setRows([]); setFileName(""); };

    const downloadTemplate = () => {
        const header = COLS.map((c) => c.header);
        const exemple = {
            "Nom": "TCHAKPA", "Prénom": "Koffi", "Sexe (M/F)": "M",
            "Date de naissance (JJ/MM/AAAA)": "12/05/1990", "Lieu de naissance": "Lomé",
            "Téléphone": "90000000", "Adresse": "Lomé", "Email": "",
            "Profession": "Chauffeur", "Cas (BLESSE/DECEDE/AUTRE)": "BLESSE",
            "Gravité blessure": "Légère", "Description": "Fracture bras",
            "Adversaire (OUI/NON)": "NON", "Pays de résidence": "TOGO",
            "Revenu mensuel avant accident": 0, "Perte revenu mensuel après": 0, "Bourse mensuelle": 0,
        };
        const ws = XLSX.utils.json_to_sheet([exemple], { header });
        ws["!cols"] = header.map(() => ({ wch: 22 }));
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Victimes");
        XLSX.writeFile(wb, "modele_victimes.xlsx");
    };

    const handleFile = (file) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const wb = XLSX.read(e.target.result, { type: "array" });
                const ws = wb.Sheets[wb.SheetNames[0]];
                const raw = XLSX.utils.sheet_to_json(ws, { defval: "" });
                const mapped = raw.map((r, i) => {
                    // index des valeurs par intitulé normalisé
                    const byNorm = {};
                    Object.entries(r).forEach(([k, v]) => { byNorm[norm(k)] = v; });
                    const out = { __rowKey: `r${i}`, __errors: [] };
                    COLS.forEach((c) => {
                        const raw0 = byNorm[norm(c.header)];
                        let val = raw0;
                        if (c.kind === "date") val = parseDate(raw0);
                        else if (c.kind === "cas") val = normCas(raw0);
                        else if (c.kind === "sexe") val = normSexe(raw0);
                        else if (c.kind === "bool") val = normBool(raw0);
                        else if (c.kind === "num") val = normNum(raw0);
                        else val = raw0 == null ? "" : `${raw0}`.trim();
                        out[c.key] = val;
                    });
                    // Validation
                    if (!out.nom) out.__errors.push("Nom obligatoire");
                    if (!["BLESSE", "DECEDE", "AUTRE"].includes(out.casVictime)) {
                        out.__errors.push("Cas invalide (BLESSE/DECEDE/AUTRE)");
                    }
                    return out;
                }).filter((r) => Object.keys(r).some((k) => !k.startsWith("__") && r[k] !== "" && r[k] != null));
                setRows(mapped);
                setFileName(file.name);
                if (!mapped.length) toast.warn("Aucune ligne exploitable dans le fichier");
            } catch (err) {
                toast.error("Fichier illisible. Utilisez le modèle fourni.");
            }
        };
        reader.readAsArrayBuffer(file);
        return false; // empêche l'upload auto d'AntD
    };

    const valides = useMemo(() => rows.filter((r) => r.__errors.length === 0), [rows]);
    const invalides = rows.length - valides.length;

    const handleSave = () => {
        if (saving) return;
        if (!sinistre?.codeId) { toast.error("Sinistre introuvable"); return; }
        if (!valides.length) { toast.warn("Aucune victime valide à enregistrer"); return; }
        setSaving(true);
        const payload = valides.map((r) => ({
            nom: r.nom, prenom: r.prenom || "", sexe: r.sexe || "",
            dateNaissance: r.dateNaissance ? r.dateNaissance.format("YYYY-MM-DD") : null,
            lieuNaissance: r.lieuNaissance || "", telephone: r.telephone || "",
            adresse: r.adresse || "", email: r.email || "", profession: r.profession || "",
            casVictime: r.casVictime, graviteBlessure: r.graviteBlessure || "",
            descriptionDegatsVictime: r.descriptionDegatsVictime || "",
            adversaire: !!r.adversaire, paysResidenceHabituel: r.paysResidenceHabituel || "",
            revenuProfessionelMensuelAvantAccident: r.revenuProfessionelMensuelAvantAccident,
            perteRevenuProfessionelMensuelApresAccident: r.perteRevenuProfessionelMensuelApresAccident,
            montantBourseMensuelle: r.montantBourseMensuelle,
        }));
        axiosInstance.post(`/api/victime/ind/batch/${sinistre.codeId}`, payload)
            .then(() => {
                toast.success(`${payload.length} victime(s) importée(s)`);
                setRefetch && setRefetch((p) => !p);
                reset();
                setOpen(false);
            })
            .catch(() => toast.error("Erreur lors de l'enregistrement des victimes"))
            .finally(() => setSaving(false));
    };

    const columns = [
        {
            title: "Statut", key: "statut", width: 90, fixed: "left",
            render: (_, r) => r.__errors.length === 0
                ? <Tag color="success" icon={<CheckCircleOutlined />}>OK</Tag>
                : (
                    <Tooltip title={r.__errors.join(" · ")}>
                        <Tag color="error" icon={<CloseCircleOutlined />}>Erreur</Tag>
                    </Tooltip>
                ),
        },
        { title: "Nom", dataIndex: "nom", key: "nom" },
        { title: "Prénom", dataIndex: "prenom", key: "prenom" },
        { title: "Sexe", dataIndex: "sexe", key: "sexe", width: 60 },
        { title: "Cas", dataIndex: "casVictime", key: "cas", width: 90 },
        { title: "Naissance", key: "dn", render: (_, r) => r.dateNaissance ? r.dateNaissance.format("DD/MM/YYYY") : "—" },
        { title: "Téléphone", dataIndex: "telephone", key: "tel" },
        { title: "Gravité", dataIndex: "graviteBlessure", key: "grav" },
        { title: "Adversaire", key: "adv", width: 90, render: (_, r) => (r.adversaire ? "Oui" : "Non") },
    ];

    return (
        <Modal
            title={<Space><FileExcelOutlined style={{ color: "#217346" }} /><span>Importer des victimes (Excel)</span></Space>}
            open={open}
            onCancel={() => { reset(); setOpen(false); }}
            width={980}
            footer={[
                <Button key="tpl" icon={<DownloadOutlined />} onClick={downloadTemplate}>Modèle</Button>,
                <Button key="cancel" onClick={() => { reset(); setOpen(false); }}>Fermer</Button>,
                <Button key="save" type="primary" style={{ background: "#242166" }}
                    icon={<UploadOutlined />} loading={saving}
                    disabled={saving || !valides.length}
                    onClick={handleSave}>
                    {saving ? "Enregistrement…" : `Enregistrer ${valides.length} victime(s)`}
                </Button>,
            ]}
        >
            <Text type="secondary" style={{ display: "block", marginBottom: 12 }}>
                Téléchargez le modèle, remplissez-le, puis importez-le. Vérifiez l'aperçu avant d'enregistrer.
                Colonnes obligatoires : <b>Nom</b> et <b>Cas</b> (BLESSE / DECEDE / AUTRE).
            </Text>

            {rows.length === 0 ? (
                <Dragger accept=".xlsx,.xls" showUploadList={false} beforeUpload={handleFile} maxCount={1}>
                    <p className="ant-upload-drag-icon"><InboxOutlined /></p>
                    <p className="ant-upload-text">Cliquez ou glissez le fichier Excel des victimes</p>
                    <p className="ant-upload-hint">Format .xlsx conforme au modèle</p>
                </Dragger>
            ) : (
                <>
                    <Space style={{ marginBottom: 8, justifyContent: "space-between", width: "100%" }}>
                        <Text><b>{fileName}</b> — {rows.length} ligne(s)</Text>
                        <Space>
                            <Tag color="success">{valides.length} valide(s)</Tag>
                            {invalides > 0 && <Tag color="error">{invalides} en erreur</Tag>}
                            <Button size="small" onClick={reset}>Changer de fichier</Button>
                        </Space>
                    </Space>
                    {invalides > 0 && (
                        <Alert
                            type="warning" showIcon style={{ marginBottom: 8 }}
                            message={`${invalides} ligne(s) en erreur seront ignorées à l'enregistrement (survolez le statut « Erreur »).`}
                        />
                    )}
                    <Table
                        size="small"
                        rowKey="__rowKey"
                        columns={columns}
                        dataSource={rows}
                        scroll={{ x: "max-content", y: 340 }}
                        pagination={{ pageSize: 50, showSizeChanger: false }}
                        rowClassName={(r) => (r.__errors.length ? "" : "")}
                    />
                </>
            )}
        </Modal>
    );
};

export default VictimesExcelImport;
